import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";
import { UploadCloud, ShieldAlert, FileJson } from "lucide-react";

export default function UploadStorageStateDialog({
  open,
  onOpenChange,
  profileName,
  onUploaded,
}) {
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  async function handleFile(e) {
    const f = e.target.files?.[0];
    setPreview(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (!f.name.endsWith(".json") || f.size === 0) {
      toast.error("Sube un fichero .json válido");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error("El fichero supera 2 MB");
      return;
    }
    try {
      const txt = await f.text();
      const parsed = JSON.parse(txt);
      const cookies = Array.isArray(parsed?.cookies) ? parsed.cookies.length : 0;
      const origins = Array.isArray(parsed?.origins) ? parsed.origins.length : 0;
      setFile(f);
      setPreview({ size: f.size, cookies, origins });
    } catch (err) {
      toast.error("El fichero no es JSON válido");
      setFile(null);
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!file) return;
    setSaving(true);
    try {
      const body = await file.text();
      const { data } = await api.post(
        `/browser/profiles/${encodeURIComponent(profileName)}/storage-state`,
        body,
        { headers: { "Content-Type": "application/json" } }
      );
      toast.success("storage_state subido");
      onUploaded?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.browser.uploadDialog}
        className="bg-zinc-950 border border-zinc-800 text-zinc-100 sm:max-w-[520px]"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <UploadCloud className="h-4 w-4" />
            Subir storage_state — {profileName}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Sube un JSON obtenido con{" "}
            <code className="font-mono text-zinc-300">
              playwright codegen --save-storage
            </code>
            . El fichero se envía a KRAI API en streaming; KRAI CONNECT no lo
            almacena.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Fichero .json</Label>
            <Input
              data-testid={TID.browser.uploadFile}
              type="file"
              accept="application/json,.json"
              onChange={handleFile}
              className="bg-zinc-900 border-zinc-800 file:text-zinc-200 file:bg-zinc-800 file:border-0 file:rounded file:mr-3 file:px-2 file:py-1"
            />
          </div>
          {preview && (
            <div className="rounded-md border border-zinc-800 bg-black/40 p-3 text-xs text-zinc-300 space-y-1 font-mono">
              <div className="flex items-center gap-2">
                <FileJson className="h-3.5 w-3.5 text-zinc-400" />
                {file?.name}
              </div>
              <div>tamaño: {preview.size} bytes</div>
              <div>cookies: {preview.cookies}</div>
              <div>origins: {preview.origins}</div>
            </div>
          )}
          <div className="rounded-md border border-amber-900/40 bg-amber-950/20 p-3 flex items-start gap-2 text-[11px] text-amber-200/80">
            <ShieldAlert className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
            El JSON contiene cookies de sesión. Manéjalo como una credencial.
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid={TID.browser.uploadSubmit}
              disabled={!file || saving}
              className="bg-white text-black hover:bg-zinc-200"
            >
              {saving ? "Subiendo…" : "Subir"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
